import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaTimes, FaGlobeAsia, FaCity } from 'react-icons/fa';
import './CropMapSearchFilter.css';

// Complete list of Indian states and UTs - moved outside component to prevent re-creation
const ALL_INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
  'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
  'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Andaman And Nicobar', 'Chandigarh', 'Dadra And Nagar Haveli',
  'Daman And Diu', 'Delhi', 'Jammu And Kashmir', 'Ladakh',
  'Lakshadweep', 'Puducherry'
];

const CropMapSearchFilter = ({ 
  statesData,
  onStateSelect,
  onDistrictSelect,
  onReset
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [allDistricts, setAllDistricts] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const searchRef = useRef(null);
  const inputRef = useRef(null);

  // Load all districts from all states on mount
  useEffect(() => {
    const loadAllDistricts = async () => {
      setLoadingDistricts(true);
      const districts = [];
      
      try {
        // Load districts for states that have data
        const statesWithData = statesData ? Object.keys(statesData) : [];
        
        for (const state of statesWithData) {
          try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || (process.env.REACT_APP_API_URL || "http://localhost:5001")))}/api/districts/${encodeURIComponent(state)}`);
            const data = await response.json();
            
            if (data.districts && Array.isArray(data.districts)) {
              data.districts.forEach(district => {
                districts.push({
                  type: 'district',
                  name: district.district,
                  displayName: `${district.district}, ${state}`,
                  stateName: state,
                  primaryCrop: district.primary,
                  secondaryCrop: district.secondary,
                  primaryProd: district.primaryProd,
                  secondaryProd: district.secondaryProd,
                  hasData: true
                });
              });
            }
          } catch (error) {
            console.error(`Error loading districts for ${state}:`, error);
          }
        }
        
        setAllDistricts(districts);
        console.log(`Loaded ${districts.length} districts from ${statesWithData.length} states`);
      } catch (error) {
        console.error('Error loading districts:', error);
      } finally {
        setLoadingDistricts(false);
      }
    };
    
    if (statesData && Object.keys(statesData).length > 0) {
      loadAllDistricts();
    }
  }, [statesData]);

  // Build search index from states and districts
  const searchIndex = useMemo(() => {
    const index = [];

    // Add all Indian states to the index
    ALL_INDIAN_STATES.forEach(stateName => {
      const hasData = statesData && statesData[stateName];
      const crops = hasData ? statesData[stateName] : null;
      
      index.push({
        type: 'state',
        name: stateName,
        displayName: stateName,
        primaryCrop: crops && crops[0] ? crops[0] : null,
        secondaryCrop: crops && crops[1] ? crops[1] : null,
        totalCrops: crops ? crops.length : 0,
        hasData: hasData
      });
    });

    // Add all districts to the index
    allDistricts.forEach(district => {
      index.push(district);
    });

    return index;
  }, [statesData, allDistricts]);

  // Filter results based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredResults([]);
      setIsOpen(false);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const results = searchIndex.filter(item => {
      if (!item || !item.name) return false;
      
      const itemName = item.name.toLowerCase();
      const primaryCrop = item.primaryCrop ? item.primaryCrop.toLowerCase() : '';
      
      // Search by state name or primary crop
      return itemName.includes(term) || primaryCrop.includes(term);
    });

    // Sort by relevance
    results.sort((a, b) => {
      const aName = (a.name || '').toLowerCase();
      const bName = (b.name || '').toLowerCase();
      
      if (aName === term) return -1;
      if (bName === term) return 1;
      if (aName.startsWith(term)) return -1;
      if (bName.startsWith(term)) return 1;
      return 0;
    });

    setFilteredResults(results.slice(0, 20));
    setIsOpen(results.length > 0);
    setHighlightedIndex(-1);
  }, [searchTerm, searchIndex]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredResults.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredResults.length) {
          handleSelect(filteredResults[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle selection
  const handleSelect = (item) => {
    if (!item) return;
    
    try {
      setIsOpen(false);
      setHighlightedIndex(-1);
      
      if (item.type === 'state') {
        if (item.name && onStateSelect) {
          onStateSelect(item.name);
          setSearchTerm(''); // Clear search after selection
        }
      } else if (item.type === 'district') {
        if (item.name && item.stateName) {
          // First select the state
          if (onStateSelect) {
            onStateSelect(item.stateName);
          }
          // Then show district details
          if (onDistrictSelect) {
            onDistrictSelect(item);
          }
          setSearchTerm(''); // Clear search after selection
        }
      }
    } catch (error) {
      console.error('Error handling selection:', error);
    }
  };

  // Clear search
  const handleClear = () => {
    setSearchTerm('');
    setFilteredResults([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (onReset) {
      onReset();
    }
    inputRef.current?.focus();
  };

  return (
    <div className="crop-search-filter" ref={searchRef}>
      <div className="crop-search-input-container">
        <FaSearch className="crop-search-icon" />
        <input
          ref={inputRef}
          type="text"
          className="crop-search-input"
          placeholder="Search states or districts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchTerm && setIsOpen(true)}
        />
        {searchTerm && (
          <button 
            className="crop-search-clear" 
            onClick={handleClear}
            aria-label="Clear search"
          >
            <FaTimes />
          </button>
        )}
      </div>

      {isOpen && filteredResults.length > 0 && (
        <div className="crop-search-dropdown">
          <div className="crop-search-results-header">
            <span>{filteredResults.length} state{filteredResults.length !== 1 ? 's' : ''} found</span>
          </div>
          <ul className="crop-search-results">
            {filteredResults.map((result, index) => (
              <li
                key={`${result.type}-${result.name}-${index}`}
                className={`crop-search-result-item ${highlightedIndex === index ? 'highlighted' : ''} ${!result.hasData ? 'no-data' : ''}`}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="crop-search-result-content">
                  {result.type === 'state' ? (
                    <FaGlobeAsia className="crop-search-result-icon state-icon" />
                  ) : (
                    <FaCity className="crop-search-result-icon district-icon" />
                  )}
                  <div className="crop-search-result-text">
                    <div className="crop-search-result-name">{result.displayName}</div>
                    <div className="crop-search-result-meta">
                      {result.hasData ? (
                        <>
                          {result.primaryCrop && (
                            <span className="crop-search-result-crop">
                              🌾 {result.primaryCrop}
                            </span>
                          )}
                          {result.type === 'state' && result.totalCrops > 0 && (
                            <span className="crop-search-result-count">
                              {result.totalCrops} crop{result.totalCrops !== 1 ? 's' : ''}
                            </span>
                          )}
                          {result.type === 'district' && (
                            <span className="crop-search-result-type-badge">
                              District
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="crop-search-result-no-data">
                          ⚠️ No data available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isOpen && searchTerm && filteredResults.length === 0 && (
        <div className="crop-search-dropdown">
          <div className="crop-search-no-results">
            <FaSearch className="crop-search-no-results-icon" />
            <p>No results found for "{searchTerm}"</p>
            <small>Try searching for a state name or crop</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default CropMapSearchFilter;